import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUpload } from '../context/UploadContext';
import { notify } from '../utils/notifications';
import '../styles/CaseManagement.css';
import api from '../api/api';
import OnboardingBanner from './OnboardingBanner';
import { useCases } from '../hooks/useCases';
import { useCaseMetrics } from '../hooks/useCaseMetrics';
import CaseListMetrics from './caselist/CaseListMetrics';
import CaseAgeBadge, { shouldShowAge } from './caselist/CaseAgeBadge';
import AdmissionStatusPicker from './caselist/AdmissionStatusPicker';
import ConfirmDialog from './workflows/shared/ConfirmDialog';


function getPatientDisplayName(structuredInfo) {
    const first = structuredInfo?.["Patient First Name"]?.answer;
    const last = structuredInfo?.["Patient Last Name"]?.answer;
    // Fall back to legacy "Patient Name" field for existing cases
    const legacy = structuredInfo?.["Patient Name"]?.answer;
    const parts = [last, first].filter(p => p && p.toLowerCase() !== 'information not found');
    if (parts.length > 0) return parts.join(', ');
    if (legacy && legacy.toLowerCase() !== 'information not found') return legacy;
    return 'Unnamed Patient';
}

function countActiveCriticalFlags(redFlags) {
    const flags = redFlags?.flags;
    if (!Array.isArray(flags)) return 0;
    // A "rejected" flag is a confirmed false positive and no longer counts.
    // "approved" critical flags still count — the issue is real, just acknowledged.
    return flags.filter(f => f?.severity === 'critical' && f?.status !== 'rejected').length;
}

function getInsuranceProvider(structuredInfo) {
    // Field name varies by extraction config: default/LTACH use "Insurance Provider",
    // home health uses "Insurance Type". Sentinels also vary ("Information not found"
    // vs "NOT FOUND"). Try each candidate and return the first real value.
    const candidates = ["Insurance Provider", "Insurance Type"];
    const missingSentinels = new Set(["information not found", "not found"]);
    for (const key of candidates) {
        const value = structuredInfo?.[key]?.answer;
        if (typeof value !== 'string') continue;
        const trimmed = value.trim();
        if (!trimmed || missingSentinels.has(trimmed.toLowerCase())) continue;
        return trimmed;
    }
    return null;
}

function CaseManagement() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const uploadCtx = useUpload();
    const [sortBy, setSortBy] = useState('lastModifiedDesc');
    const [deleteTarget, setDeleteTarget] = useState(null);

    const { cases, loading: isLoading, error: casesError } = useCases(user?.uid);
    const metrics = useCaseMetrics(cases);

    if (casesError) {
        // Surface to console — Sentry catches it via the global ErrorBoundary.
        // eslint-disable-next-line no-console
        console.error('useCases listener error:', casesError);
    }

    const handleNewCase = () => {
        navigate('/case/new', { replace: true });
    };

    const handleEditCase = (caseId) => {
        navigate(`/case/${caseId}`);
    };

    const isProcessing = (caseItem) => {
        // Case is processing if status.condition is 'working'
        if (caseItem.status?.condition === 'working') {
            return true;
        }
        // Or if upload context is actively uploading for this case
        if (uploadCtx.caseId === caseItem.id) {
            const hasActiveFile = uploadCtx.files.some(
                f => f.status === 'pending' || f.status === 'uploading' || f.status === 'stalled'
            );
            if (hasActiveFile) {
                return true;
            }
        }
        return false;
    };

    const handleDeleteClick = (caseItem) => {
        setDeleteTarget(caseItem);
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            const formData = new FormData();
            formData.append('case_id', deleteTarget.id);

            // Call the backend endpoint to delete the case and its file
            await api.delete('/case/delete', {
                data: formData
            });
            // No local state mutation: useCases onSnapshot will receive the
            // delete event and update `cases` automatically.
        } catch (error) {
            console.error('Error deleting case:', error);
            notify.error('Failed to delete case. Please try again.');
        } finally {
            setDeleteTarget(null);
        }
    };

    const handleCancelDelete = () => {
        setDeleteTarget(null);
    };

    if (isLoading) {
        return <div>Loading...</div>;
    }

    // useCaseMetrics intentionally consumes the unfiltered `cases` array so its
    // aggregate counts and the closed-trend chart stay correct. The render layer
    // filters archived cases out of the visible list.
    const visibleCases = cases.filter((c) => !c.archived);

    const sortedCases = (() => {
        const copy = [...visibleCases];
        if (sortBy === 'patientNameAsc') {
            return copy.sort((a, b) => {
                const nameA = (getPatientDisplayName(a.structuredInfo)).toLowerCase();
                const nameB = (getPatientDisplayName(b.structuredInfo)).toLowerCase();
                return nameA.localeCompare(nameB);
            });
        }
        if (sortBy === 'admissionStatus') {
            const order = [
                'In progress',
                'Submitted to Insurance',
                'Insurance Approved',
                'Insurance Denied',
                'Rejected',
                'Admitted',
                'Closed',
            ];
            const indexOf = (label) => {
                const norm = (label || 'In progress').toString();
                const idx = order.indexOf(norm);
                return idx === -1 ? order.indexOf('In progress') : idx;
            };
            return copy.sort((a, b) => indexOf(a.admission_status) - indexOf(b.admission_status));
        }
        if (sortBy === 'createdDesc') {
            return copy.sort((a, b) => {
                const dateA = a.createdAt?.toMillis?.() ?? 0;
                const dateB = b.createdAt?.toMillis?.() ?? 0;
                return dateB - dateA;
            });
        }
        if (sortBy === 'caseAgeDesc') {
            return copy.sort((a, b) => {
                const msA = a.createdAt?.toMillis?.();
                const msB = b.createdAt?.toMillis?.();
                // Cases without a creation date sort to the bottom
                if (msA == null && msB == null) return 0;
                if (msA == null) return 1;
                if (msB == null) return -1;
                return msA - msB; // ascending creation date = oldest (most urgent) first
            });
        }
        if (sortBy === 'insuranceAsc') {
            return copy.sort((a, b) => {
                const insA = getInsuranceProvider(a.structuredInfo);
                const insB = getInsuranceProvider(b.structuredInfo);
                // Cases without insurance sort to the bottom
                if (!insA && !insB) return 0;
                if (!insA) return 1;
                if (!insB) return -1;
                return insA.toLowerCase().localeCompare(insB.toLowerCase());
            });
        }
        // Default: last modified, newest first
        return copy.sort((a, b) => {
            const dateA = a.last_modified_date?.toDate() || new Date(0);
            const dateB = b.last_modified_date?.toDate() || new Date(0);
            return dateB - dateA;
        });
    })();

    return (
        <div className="case-management">
            <div className="case-management-header">
                <h1>My Cases</h1>
                <div className="header-actions">
                    <button
                        className="new-case-button"
                        onClick={handleNewCase}
                    >
                        Create New Case
                    </button>
                </div>
            </div>

            <OnboardingBanner />

            <CaseListMetrics metrics={metrics} loading={isLoading} />

            {visibleCases.length > 0 && (
                <div className="case-list-controls">
                    <label className="sort-control">
                        <span className="sort-label">Sort:</span>
                        <select
                            className="sort-select"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="lastModifiedDesc">Last modified (newest first)</option>
                            <option value="createdDesc">Created (newest first)</option>
                            <option value="caseAgeDesc">Filing window (most elapsed first)</option>
                            <option value="patientNameAsc">Patient Name (A–Z)</option>
                            <option value="admissionStatus">Admission status</option>
                            <option value="insuranceAsc">Insurance (A–Z)</option>
                        </select>
                    </label>
                </div>
            )}

            <div className="cases-list">
                {visibleCases.length === 0 ? (
                    <div className="no-cases">
                        <p>You haven't created any cases yet.</p>
                        <button onClick={handleNewCase}>Create your first case</button>
                    </div>
                ) : (
                    sortedCases.map(caseItem => (
                        <div key={caseItem.id} className="case-item">
                            <div className="case-info">
                                <div className="case-title-row">
                                    <h3>{getPatientDisplayName(caseItem.structuredInfo)}</h3>
                                    {(() => {
                                        const criticalCount = countActiveCriticalFlags(caseItem.redFlags);
                                        if (criticalCount === 0) return null;
                                        const label = `${criticalCount} critical red flag${criticalCount === 1 ? '' : 's'}`;
                                        return (
                                            <span
                                                className="case-critical-flag"
                                                role="img"
                                                aria-label={label}
                                                title={label}
                                            >
                                                🚩
                                            </span>
                                        );
                                    })()}
                                    <CaseAgeBadge
                                        createdAt={caseItem.createdAt}
                                        admissionStatus={caseItem.admission_status}
                                        archived={false}
                                    />
                                </div>
                                <div className="case-meta">
                                    <AdmissionStatusPicker
                                        caseId={caseItem.id}
                                        currentStatus={caseItem.admission_status}
                                        archived={false}
                                    />
                                    {getInsuranceProvider(caseItem.structuredInfo) && (
                                        <>
                                            <span className="meta-separator">•</span>
                                            Insurance: {getInsuranceProvider(caseItem.structuredInfo)}
                                        </>
                                    )}
                                    {shouldShowAge(caseItem.createdAt, caseItem.admission_status, false) && (
                                        <>
                                            <span className="meta-separator">•</span>
                                            Created: {new Date(caseItem.createdAt.toMillis()).toLocaleDateString()}
                                        </>
                                    )}
                                    <span className="meta-separator">•</span>
                                    Last modified: {caseItem.last_modified_date ?
                                        new Date(caseItem.last_modified_date.toDate()).toLocaleString() :
                                        'N/A'}
                                </div>
                            </div>
                            <div className="case-actions">
                                <button
                                    onClick={() => handleEditCase(caseItem.id)}
                                    className="edit-button"
                                >
                                    Open
                                </button>
                                <button
                                    onClick={() => handleDeleteClick(caseItem)}
                                    className="delete-button"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {deleteTarget && (
                <ConfirmDialog
                    open={Boolean(deleteTarget)}
                    title={
                        isProcessing(deleteTarget)
                            ? 'Delete case while processing?'
                            : 'Delete this case?'
                    }
                    message={
                        isProcessing(deleteTarget)
                            ? 'This case is still processing. Deleting now will discard the extraction in progress.'
                            : 'Are you sure you want to delete this case?'
                    }
                    confirmLabel={
                        isProcessing(deleteTarget)
                            ? 'Delete anyway'
                            : 'Delete'
                    }
                    danger={true}
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                />
            )}
        </div>
    );
}

export default CaseManagement;
